import React, { memo } from 'react';

import { makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';

import {
  Checkbox,
  Image,
  RadioButton,
  Typography,
} from '../../../components/atoms';
import * as Api from '../../../weborder/Api';
import { ToCurrency } from '../../../weborder/BasketControl';

interface Props {
  currency: Api.Currency;
  imageUrl: string;
  isAvailable: boolean;
  isMasterOptionSet: boolean;
  isSelected: boolean;
  isSingleChoice: boolean;
  languageCode: string;
  menuItemOptionSetItemId: number;
  name: string;
  onCheckboxChange: any;
  price: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  optionImage: {
    objectFit: 'cover',
  },

  optionLabel: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',

    '& .fd-text': {
      margin: 0,
      padding: 0,
    },
  },
  optionLabelDescription: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',

    '& .fd-text': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  optionLabelImage: {
    flex: '1 0 auto',
    height: 65,
    marginRight: theme.spacing(1.5),
    width: 65,
  },

  optionsList: {
    margin: theme.spacing(0, 3),
    padding: 0,
  },
  optionListItem: {
    padding: theme.spacing(1.25, 0),
  },

  optionPrice: {
    minWidth: theme.spacing(8),
    textAlign: 'right',
  },

  optionsSet: {
    paddingBottom: theme.spacing(2),

    '& label': {
      margin: 0,
      padding: 0,
    },

    '& .fd-text__h2': {
      padding: theme.spacing(2.5, 2, 1.75),
    },
  },

  [theme.breakpoints.up('sm')]: {
    optionsSet: {
      '& .fd-text__h2': {
        fontSize: theme.typography.pxToRem(21),
        padding: theme.spacing(2.5, 3, 1.75),
      },
    },
  },
}));

function OptionInputComponent(props: Props): JSX.Element {
  const {
    currency,
    imageUrl,
    isAvailable,
    isMasterOptionSet,
    isSelected,
    isSingleChoice,
    languageCode,
    menuItemOptionSetItemId,
    name,
    onCheckboxChange,
    price,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme?.breakpoints.down('xs'));

  if (!isAvailable) {
    return null;
  }

  const renderOptionPrice = (): JSX.Element | null => {
    if (!price) {
      return null;
    }

    const prefix = isMasterOptionSet ? '' : '+';
    return (
      <Typography className={classes.optionPrice}>
        {`${prefix}${ToCurrency(price, currency, languageCode)}`}
      </Typography>
    );
  };

  const showImage = imageUrl !== null && !isSmallScreen;

  const menuOptionLabel = (
    <div className={classes.optionLabel}>
      <div className={classes.optionLabelDescription}>
        {showImage && (
          <div className={classes.optionLabelImage}>
            <Image
              alt={name}
              className={classes.optionImage}
              dimensions={[65, 65]}
              isInOverflowContainer
              src={imageUrl}
            />
          </div>
        )}

        <Typography>{name}</Typography>
      </div>

      {renderOptionPrice()}
    </div>
  );

  return (
    <li className={classes.optionListItem} key={menuItemOptionSetItemId}>
      {isSingleChoice ? (
        <RadioButton
          checked={isSelected}
          key={menuItemOptionSetItemId}
          label={menuOptionLabel}
          value={menuItemOptionSetItemId}
        />
      ) : (
        <Checkbox
          checked={isSelected}
          iconSize="s"
          key={menuItemOptionSetItemId}
          label={menuOptionLabel}
          onChange={onCheckboxChange}
          value={menuItemOptionSetItemId}
        />
      )}
    </li>
  );
}

const OptionInput = memo(OptionInputComponent);
OptionInput.displayName = 'OptionInput';

export { OptionInput };
